import Box from '@material-ui/core/Box';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import _debounce from 'lodash.debounce';
import * as React from 'react';

import { SearchField } from '../models/SearchField';
import OperatorDropdownButton from './OperatorDropdownButton';
import RemoveFieldButton from './RemoveFieldButton';
import TextDropdownButton from './TextDropdownButton';

export interface Props {
  onChangeField: (searchField: Partial<SearchField>) => void;
  onRemoveField: (searchFieldId: string) => void;
  onSelectReplaceField: (newFieldName: Pick<SearchField, 'fieldName' | 'id'>) => void;

  availableNewOperators: string[];
  isOperatorDisabled: boolean;
  fieldOptions: string[];
  searchField: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: 48, // TODO. This could be moved to theme. Maybe use toolbar min-height?
      '&:hover $removeButton': {
        display: 'block' // Show remove button on hover, hidden by default
      }
    },
    textButton: {
      flex: '1 1 0%',
      margin: 6, // Hardcode to get exact spacing needed regardless of theme
      alignSelf: 'stretch'
    },
    operator: {
      flex: '0 0 auto'
    },
    textField: {
      flex: '1.5 1 0%',
      marginLeft: 12,
      marginRight: 14 // Match left spacing of field text button
    },
    input: {
      ...theme.typography.body1
    },
    removeButton: {
      display: 'none',
      position: 'absolute',
      right: -14
    }
  })
);

const TextSearchField: React.FC<Props> = ({
  onChangeField,
  onRemoveField,
  onSelectReplaceField,
  availableNewOperators,
  isOperatorDisabled,
  fieldOptions,
  searchField
}) => {
  const classes = useStyles();
  const inputRef: React.MutableRefObject<any> = React.useRef();

  React.useEffect(() => {
    if (!searchField || !inputRef || !inputRef.current) {
      return;
    }
    const timeout = setTimeout(() => {
      inputRef.current.focus();
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchField]);

  const onChange = (key, changedValue) => {
    if (key === 'fieldName') {
      onSelectReplaceField({
        id: searchField.id,
        fieldName: changedValue
      });
      return;
    }
    onChangeField({
      id: searchField.id,
      [key]: changedValue
    });
  };

  const debounce = _debounce((key, value) => onChange(key, value), 300);

  const onUserInput = (key, e) => {
    debounce(key, e.target.value);
  };

  return (
    <Box
      className={classes.root}
      component="section"
      position="relative"
      display="flex"
      flexDirection="row"
      alignItems="center"
      bgcolor="background.default"
    >
      <TextDropdownButton
        className={classes.textButton}
        currentOption={searchField.fieldName}
        availableOptions={fieldOptions}
        onSelect={onChange.bind(null, 'fieldName')}
      />
      <OperatorDropdownButton
        className={classes.operator}
        currentOption={searchField.operator}
        availableOptions={availableNewOperators}
        onSelect={onChange.bind(null, 'operator')}
        disabled={isOperatorDisabled}
      />
      <TextField
        className={classes.textField}
        inputRef={inputRef}
        id="standard-bare"
        margin="normal"
        onChange={onUserInput.bind(null, 'value')}
        inputProps={{
          className: classes.input,
          placeholder: searchField.fieldName,
          'aria-label': 'bare'
        }}
      />
      <RemoveFieldButton
        className={classes.removeButton}
        onClick={onRemoveField}
        searchFieldId={searchField.id}
      />
    </Box>
  );
};

export default TextSearchField;
